<template>
  <div ref="timeline"
       :style="{ background: 'linear-gradient(to right, ' + gradientCSS + ')' }"
       class="timeline border-primary my-10">

    <!-- === MARQUEURS D'ANNEE === -->
    <div v-for="year in yearMarkers" :key="'year_' + year.year" :style="{ left: year.position + '%' }" class="year-marker">
      <div class="year-line"></div>
      <div class="year-label fw-bolder text-gray-400 fs-2">{{ year.year }}</div>
    </div>

    <!-- === AVENANTS === -->
    <div v-for="(pos, index) in mergedPositions" :key="'timeline-position_'+pos.uuid" :class="{'top': index % 2 === 0}"
         :style="{ left: pos.position + '%' }"
         class="timeline-point">
      <div :class="{
           'bg-success': pos.status === 'EN_COURS' || pos.status === 'VALIDE',
           'bg-danger': pos.status === 'EXPIRE',
           'bg-warning': pos.status === 'PROJET',
           'border border-info border-2': pos.avenants.length > 1
         }" class="zoom bg-success" @click="goToPosition(pos)"></div>

      <div :key="'anchor_'+pos.dateEffet" class="timeline-anchor text-muted fw-bolder fs-8" @click="goToPosition(pos)">
        {{ datify(pos.dateEffet, 'dM') }}
      </div>

      <div class="timeline-tooltip p-5 fs-8" @click="goToPosition(pos)">
        <div class="bg-white shadow-sm rounded">
          <template v-for="(av, avIndex) in pos.avenants">
            <div v-if="av.uuid != '__today__'" :key="'tooltip_'+av.uuid" :class="{ 'border-top': avIndex > 0 }" class="px-5 py-3 bg-hover-light-primary d-flex align-items-center justify-content-between gap-5"
                 @click="goToAvenant(av,(avIndex>0 ? pos.avenants[0] : null))">
              <div>
                <div class="fw-bolder">{{ datify(av.dateEffet, 'dmY H:i:s') }}</div>
                <div>
                  <span class="text-primary fw-bolder">AV{{ av.numero }}</span>
                  <span class="px-1">▪</span>
                  <span class="fw-bolder text-uppercase">
                  {{ av.type || av.avenantType?.label }}
                </span>
                </div>
                <div class="d-flex gap-5 align-items-center">
                  <div class="fw-bolder text-primary">
                    {{ insurers[av.insurer]?.label }}
                  </div>
                  <div class="text-muted fw-bolder">{{ av.police }}</div>
                </div>
              </div>
              <div>
                <i class="fad fa-angle-right fs-3 text-muted"></i>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>

    <div :style="{ left: pointerPosition + '%'}" class="position-absolute w-25px h-15px bg-success rounded timeline-pointer"
         style="z-index: 2;pointer-events: none;">
      <div
          style="bottom:30px;left:50%;transform: translateX(-50%);position: absolute;">
        <i class="fad  fa-arrow-alt-circle-down fs-3 text-success"></i>
      </div>
    </div>

  </div>
</template>

<script lang="ts">
import {Component, Prop, Vue, Watch} from 'vue-property-decorator';
import {helper} from "@/services/Helper";

@Component
export default class HorizontalTimeline extends Vue {
  @Prop({required: true, type: Array}) avenants!: any[];
  @Prop({required: true, type: Object}) insurers!: any;
  @Prop({required: true, type: Object}) contract!: any;
  @Prop() current?: string;
  @Prop() value!: string | null;

  internalDate: string | null = this.value || null;
  timelineWidth = 0;
  replacer = null

  // Computed
  get sortedavenants() {
    return [...this.avenants].sort(
        (a, b) => new Date(a.dateEffet).getTime() - new Date(b.dateEffet).getTime()
    );
  }

  get minDate() {
    return new Date(Math.min(...this.sortedavenants.map(i => new Date(i.dateEffet).getTime())));
  }

  get maxDate() {
    return new Date(Math.max(...this.sortedavenants.map(i => new Date(i.dateEffet).getTime())));
  }

  get positions() {
    // base avenants
    const base = [...this.sortedavenants];

    if (this.contract?.expireAt) {
      const expireKey = new Date(this.contract.expireAt).toISOString().slice(0, 10);
      const hasExpireAt = base.some(a =>
          a.dateEffet.startsWith(expireKey)
      );

      if (!hasExpireAt) {
        base.push({
          uuid: '__expire__',
          dateEffet: new Date(this.contract.expireAt).toISOString(),
          status: 'EXPIRE_AT'
        });
      }
    }
    // --- AJOUT TODAY AVANT CALCUL (seulement si le contrat n'est pas expiré) ---
    const today = new Date();
    const isExpired = this.contract?.expireAt && new Date(this.contract.expireAt).getTime() < today.getTime();

    if (!isExpired) {
      const todayKey = today.toISOString().slice(0, 10);
      const hasToday = base.some(a =>
          a.dateEffet.startsWith(todayKey)
      );
      if (!hasToday) {
        base.push({
          uuid: '__today__',
          dateEffet: today.toISOString(),
          status: 'TODAY'
        });
      }
    }

    const times = base.map(i => new Date(i.dateEffet).getTime());
    const minTime = Math.min(...times);
    const maxTime = Math.max(...times);
    const range = maxTime - minTime || 1;

    // --- POSITIONS ---
    return base
        .map(item => {
          const time = new Date(item.dateEffet).getTime();
          const percent = ((time - minTime) / range) * 100;
          return {
            ...item,
            position: percent
          };
        })
        .sort(
            (a, b) =>
                new Date(a.dateEffet).getTime() -
                new Date(b.dateEffet).getTime()
        );
  }

// === Propriété calculée pour les années ===
  get yearMarkers() {
    if (!this.positions.length) return [];

    // Range temporelle
    const dates = this.positions.map(p => new Date(p.dateEffet).getTime());
    const minTime = Math.min(...dates);
    const maxTime = Math.max(...dates);
    const timeRange = maxTime - minTime;

    if (timeRange === 0) return [];

    const timeToPercent = (timestamp: number) => {
      return ((timestamp - minTime) / timeRange) * 100;
    };

    const markers: any[] = [];
    const startYear = new Date(minTime).getFullYear();
    const endYear = new Date(maxTime).getFullYear();

    for (let year = startYear; year <= endYear; year++) {
      const yearStart = new Date(year, 0, 1).getTime(); // 1er janvier

      // Vérifier que l'année est dans la range visible
      if (yearStart >= minTime && yearStart <= maxTime) {
        markers.push({
          year: year,
          position: timeToPercent(yearStart)
        });
      }
    }

    return markers;
  }

  get mergedPositions() {
    const timeline = this.$refs.timeline as HTMLElement;

    if (!this.timelineWidth) {
      return this.positions.map(p => ({
        ...p,
        key: `tmp_${p.uuid || p.dateEffet}`,
        avenants: [p]
      }));
    }
    // if (!timeline) {
    //   console.log('no timeline')
    //   return this.positions.map(p => ({...p, avenants: [p]}));
    // }

    const timelineWidth = timeline.offsetWidth;
    const MIN_DISTANCE_PX = 20;

    // positions en pixels
    const positionsInPx = this.positions.map(item => ({
      ...item,
      px: (item.position / 100) * timelineWidth
    }));

    const merged: any[] = [];

    positionsInPx.forEach(item => {
      const last = merged[merged.length - 1];

      if (last && Math.abs(last.px - item.px) < MIN_DISTANCE_PX) {
        last.avenants.push(item);

        last.dateEffet = last.avenants.reduce(
            (min, a) => new Date(a.dateEffet).getTime() < new Date(min).getTime() ? a.dateEffet : min,
            last.dateEffet
        );

        last.status = last.avenants[0].status;
      } else {
        merged.push({
          uuid: helper.generateId(),
          px: item.px,
          position: item.position,
          dateEffet: item.dateEffet,
          status: item.status,
          avenants: [item]
        });
      }
    });

    return merged;
  }

  get gradientCSS() {

    return this.positions
        .map(item => {
          let colorVar = 'var(--bs-secondary)';
          switch (item.status) {
            case 'EN_COURS':
            case 'VALIDE':
              colorVar = 'var(--bs-success)';
              break;
            case 'EXPIRE':
              colorVar = 'var(--bs-danger)';
              break;
            case 'PROJET':
              colorVar = 'var(--bs-warning)';
              break;
          }
          return `${colorVar} ${item.position}%`;
        })
        .join(', ');
  }

  get pointerPosition() {
    if (!this.internalDate) return 0;

    const minTime = this.minDate.getTime();
    const maxTime = this.maxDate.getTime();
    const range = maxTime - minTime;

    if (range === 0) {
      return 0;
    }

    let percent = ((new Date(this.internalDate).getTime() - minTime) / range) * 100;
    percent = Math.min(Math.max(percent, 0), 100);
    return percent;
  }

  goToAvenant(avenant: any, replacer: any) {
    // if (replacer) {
    //   this.replacer = replacer.dateEffet
    // }
    this.$emit('input', avenant.dateEffet);

  }

  goToDate(date: any) {
    this.$emit('input', date);
  }

  goToPosition(position: any) {
    if (position.avenants.length > 0) {
      // this.replacer = position.avenants[0]?.dateEffet
      this.$emit('input', position.avenants[position.avenants?.length - 1]?.dateEffet);
    }
  }

  mounted() {
    // Si aucune date sélectionnée (v-model vide), on prend la dernière date
    if (!this.internalDate && this.sortedavenants.length > 0) {
      const lastItem = this.sortedavenants[this.sortedavenants.length - 1];
      this.internalDate = lastItem.dateEffet;
      this.$emit('input', this.internalDate);
    }

    this.$nextTick(() => {
      const el = this.$refs.timeline as HTMLElement;
      if (el) {
        this.timelineWidth = el.offsetWidth;
      }
    });
    this.$addListener(window, 'resize', this.updateTimelineWidth)
  }

  updateTimelineWidth() {
    const el = this.$refs.timeline as HTMLElement;
    if (el) {
      this.timelineWidth = el.offsetWidth;
    }
  }

  @Watch('value')
  onValueChanged(val: string | null) {
    if (this.replacer) {
      this.internalDate = this.replacer
      this.replacer = null
    } else {
      this.internalDate = val;
    }
  }

  // Méthodes
  onInputChange(date: string) {
    this.internalDate = date;
    this.$emit('input', date); // v-model
  }

  scrollToNext() {
    const timeline = this.$refs.timeline as HTMLElement;
    const scrollLeft = timeline.scrollLeft;
    const next = this.positions.find(
        p => (p.position / 100) * timeline.offsetWidth > scrollLeft
    );
    if (next)
      timeline.scrollTo({left: (next.position / 100) * timeline.offsetWidth, behavior: 'smooth'});
  }

  scrollToPrev() {
    const timeline = this.$refs.timeline as HTMLElement;
    const scrollLeft = timeline.scrollLeft;
    const prev = [...this.positions]
        .reverse()
        .find(p => (p.position / 100) * timeline.offsetWidth < scrollLeft);
    if (prev)
      timeline.scrollTo({left: (prev.position / 100) * timeline.offsetWidth, behavior: 'smooth'});
  }
}
</script>

<style scoped>

@property --bg {
  syntax: '<color>';
  inherits: false;
  initial-value: #198754;
}

.timeline-container {
  width: 100%;
  position: relative;
  margin: 20px 0;
  z-index: 2;
}

.timeline-input {
  margin-bottom: 10px;
  display: block;
}

.timeline {
  position: relative;
  width: 100%;
  height: 2px;
  //border-top: 2px solid #ccc;
  scroll-snap-type: x mandatory;
  white-space: nowrap;
}

.timeline-point {
  position: absolute;
  top: 50%;
  width: 15px;
  height: 15px;
  transform: translate(-50%, -50%);
  cursor: pointer;
  scroll-snap-align: center;
  z-index: 2;

}

.zoom {
  width: 15px;
  height: 15px;
  border-radius: 50%;
  transition: all .5s ease;
  transform-origin: center center;
}

.timeline-point:hover .zoom {
  transform: scale(1.5);
}

.timeline-pointer {
  top: 50%;
  transform: translate(-50%, -50%);
  cursor: pointer;
}

.timeline-tooltip {
  position: absolute;
  left: 0;
  top: 10px;
  bottom: auto;
  transform: translate(-50%);
  display: none;
}

.timeline-point:hover .timeline-tooltip {
  display: block;
}

.timeline-anchor {
  position: absolute;
  left: 0;
  top: 15px;
  bottom: auto;
  transform: translate(-50%);
}

.timeline-point.top .timeline-anchor {
  top: auto;
  bottom: 15px;
}

button {
  margin: 5px;
}

.year-marker {
  position: absolute;
  z-index: 2;
}

.year-line {
  border: 1px gray dashed;
  width: 0;
  height: 30px;
}

.year-label {
  transform: translateX(-50%);
}

.timeline-pointer {
  top: 50%;
  transform: translate(-50%, -50%);
  cursor: pointer;

  --bg: #198754;

  background-color: var(--bg);

  animation:
      bgShift 2.2s ease-in-out infinite,
      glowPulse 1.6s ease-in-out infinite;
}
@keyframes bgShift {
  0% {
    --bg: #198754;
  }
  50% {
    --bg: #20c997;
  }
  100% {
    --bg: #198754;
  }
}

@keyframes glowPulse {
  0% {
    box-shadow: 0 0 0 0 rgba(25, 135, 84, 0.7);
  }
  70% {
    box-shadow: 0 0 0 14px rgba(25, 135, 84, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(25, 135, 84, 0);
  }
}
</style>
